import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import type React from "react";
import { lazy, Suspense, useState } from "react";
import { useTranslation } from "react-i18next";
import { DownloadAction } from "@/components/common/media-preview/DownloadAction";
import type { MediaFile } from "@/components/common/media-preview/types";
import {
  getMediaDownloadUrl,
  getMediaKind,
  getMediaPreviewUrl,
  hasFreshSignedMediaUrl,
} from "@/components/common/media-preview/utils";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useFileByGuid } from "@/hooks/useFileByGuid";
import { cn } from "@/lib/utils";

const PdfPreview = lazy(() =>
  import("@/components/common/media-preview/PdfPreview").then((module) => ({
    default: module.PdfPreview,
  })),
);

interface MediaPreviewDialogProps {
  files: MediaFile[];
  selectedIndex: number | null;
  onSelectedIndexChange: (nextIndex: number | null) => void;
}

export const MediaPreviewDialog: React.FC<MediaPreviewDialogProps> = ({
  files,
  selectedIndex,
  onSelectedIndexChange,
}) => {
  const { t } = useTranslation();
  const [failedImageUrl, setFailedImageUrl] = useState<string | null>(null);
  const selectedFile =
    selectedIndex !== null && selectedIndex >= 0 && selectedIndex < files.length
      ? files[selectedIndex]
      : null;
  const selectedKind = selectedFile ? getMediaKind(selectedFile) : null;
  const selectedPreviewUrl = selectedFile
    ? getMediaPreviewUrl(selectedFile)
    : null;
  const hasFreshSignedUrl = selectedFile
    ? hasFreshSignedMediaUrl(selectedFile)
    : false;
  const shouldUseDirectPreviewUrl = Boolean(
    selectedFile?.preview_url && hasFreshSignedUrl,
  );
  const selectedDownloadUrl = selectedFile
    ? getMediaDownloadUrl(selectedFile)
    : null;
  const shouldLoadPdf =
    selectedKind === "pdf" &&
    Boolean(selectedFile?.guid) &&
    !shouldUseDirectPreviewUrl;
  const {
    fileBlobUrl: pdfPreviewUrl,
    isLoading: isPdfLoading,
    error: pdfError,
  } = useFileByGuid(selectedFile?.guid, shouldLoadPdf);
  const effectivePreviewUrl =
    selectedKind === "pdf" && !shouldUseDirectPreviewUrl
      ? pdfPreviewUrl
      : selectedPreviewUrl;

  const hasPrev = selectedIndex !== null && selectedIndex > 0;
  const hasNext =
    selectedIndex !== null &&
    selectedIndex >= 0 &&
    selectedIndex < files.length - 1;

  const renderViewer = () => {
    if (!selectedFile) {
      return (
        <div className="text-sm text-gray-500">
          {t("common.previewNotAvailable")}
        </div>
      );
    }

    if (
      !effectivePreviewUrl ||
      (selectedKind === "image" && failedImageUrl === effectivePreviewUrl)
    ) {
      if (selectedKind === "pdf" && isPdfLoading) {
        return (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Loader2 className="size-4 animate-spin" />
            {t("common.loading")}
          </div>
        );
      }

      if (selectedKind === "pdf" && pdfError) {
        return (
          <div className="text-sm text-gray-500">
            {t("common.previewNotAvailable")}
          </div>
        );
      }

      return (
        <div className="text-sm text-gray-500">
          {t("common.previewNotAvailable")}
        </div>
      );
    }

    if (selectedKind === "image") {
      return (
        <img
          src={effectivePreviewUrl}
          alt={selectedFile.file_name}
          className="max-h-full max-w-full object-contain"
          onError={() => setFailedImageUrl(effectivePreviewUrl)}
        />
      );
    }

    if (selectedKind === "video") {
      return (
        <video
          src={effectivePreviewUrl}
          controls
          className="max-h-full max-w-full"
        >
          <source src={effectivePreviewUrl} type={selectedFile.mime_type} />
          <track kind="captions" />
          {t("common.browserDoesNotSupportVideoTag")}
        </video>
      );
    }

    if (selectedKind === "audio") {
      return (
        <audio src={effectivePreviewUrl} controls className="w-full max-w-xl">
          <source src={effectivePreviewUrl} type={selectedFile.mime_type} />
          <track kind="captions" />
          {t("common.browserDoesNotSupportAudioTag")}
        </audio>
      );
    }

    if (selectedKind === "pdf") {
      return (
        <Suspense
          fallback={
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Loader2 className="size-4 animate-spin" />
              {t("common.loading")}
            </div>
          }
        >
          <PdfPreview
            fileName={selectedFile.file_name}
            fileUrl={effectivePreviewUrl}
          />
        </Suspense>
      );
    }

    return (
      <div className="text-sm text-gray-500">
        {t("common.previewNotAvailable")}
      </div>
    );
  };

  return (
    <Dialog
      open={selectedIndex !== null}
      onOpenChange={(open) => {
        if (!open) onSelectedIndexChange(null);
      }}
    >
      <DialogContent
        className={cn(
          "max-h-[92vh] overflow-hidden p-0",
          selectedKind === "pdf" ? "max-w-[96vw]" : "max-w-4xl",
        )}
      >
        <DialogTitle className="sr-only">
          {selectedFile?.file_name ?? t("common.previewNotAvailable")}
        </DialogTitle>

        <div
          className={cn(
            "relative flex flex-col bg-white",
            selectedKind === "pdf" ? "h-[88vh]" : "h-[80vh]",
          )}
        >
          {selectedFile && (
            <div className="flex items-center justify-between gap-3 border-b py-2 pl-4 pr-14">
              <div className="truncate text-sm font-medium text-gray-900">
                {selectedFile.file_name}
              </div>
              <DownloadAction
                guid={selectedFile.guid}
                fileName={selectedFile.file_name}
                downloadUrl={selectedDownloadUrl}
                useDirectDownload={hasFreshSignedUrl}
                accessUrlExpiresAt={selectedFile.access_url_expires_at}
                className="shrink-0 rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
              />
            </div>
          )}

          <div className="flex flex-1 items-center justify-center overflow-auto p-4">
            {renderViewer()}
          </div>

          {selectedIndex !== null && files.length > 1 && (
            <>
              <Button
                variant="ghost"
                size="icon"
                className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 shadow hover:bg-white"
                disabled={!hasPrev}
                onClick={() =>
                  hasPrev &&
                  onSelectedIndexChange(Math.max(0, selectedIndex - 1))
                }
              >
                <ChevronLeft className="size-5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 shadow hover:bg-white"
                disabled={!hasNext}
                onClick={() =>
                  hasNext &&
                  onSelectedIndexChange(
                    Math.min(files.length - 1, selectedIndex + 1),
                  )
                }
              >
                <ChevronRight className="size-5" />
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
